import {
  SelectInput,
  TextArea,
  Typography
} from "@dealer-center/core-components";
import { Locale } from "@dealer-center/ddc-core-utils";
import "./DescriptionsPanel.scss";
import { useCallback, useContext, useState } from "react";
import React from "react";
import type { IncentiveContentDefault } from "@dealer-center/ddc-pages-incentives-manager-lambda";
import { getIncentiveTypesDropdown } from "../utils/utils";
import { prohibitedContentValidation } from "../utils/validation";
import { PreferencesContext } from "../providers/PreferencesProvider";

interface DescriptionsPanelProps {
  descriptions?: IncentiveContentDefault[];
  onDescriptionsChanged?: (descriptions: IncentiveContentDefault[]) => void;
  changeError?: (id: string, status: boolean) => void;
}

export const getTypeDescription = (
  type: string,
  descriptions: IncentiveContentDefault[]
) => {
  if (!descriptions) return "";
  const description = descriptions.find(desc => desc.incentiveType === type);
  return description ? description.content : "";
};
export const getTypeDisclaimer = (
  type: string,
  descriptions: IncentiveContentDefault[]
) => {
  if (!descriptions) return "";
  const description = descriptions.find(desc => desc.incentiveType === type);
  return description ? description.disclaimer : "";
};
export const updateDescription = (
  type: string,
  value: string,
  descriptions: IncentiveContentDefault[]
) => {
  const clonedIncentives = JSON.parse(
    JSON.stringify(descriptions)
  ) as IncentiveContentDefault[];
  const existing = clonedIncentives.find(desc => desc.incentiveType === type);
  if (existing) {
    existing.content = value;
  }
  return clonedIncentives;
};
export const updateDisclaimer = (
  type: string,
  value: string,
  descriptions: IncentiveContentDefault[]
) => {
  const clonedIncentives = JSON.parse(
    JSON.stringify(descriptions)
  ) as IncentiveContentDefault[];
  const existing = clonedIncentives.find(desc => desc.incentiveType === type);
  if (existing) {
    existing.disclaimer = value;
  }
  return clonedIncentives;
};

export const DescriptionsPanel = ({
  descriptions,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onDescriptionsChanged = (descriptions: IncentiveContentDefault[]) => {},
  changeError = (id: string, status: boolean) => {
    /* no-op */
  }
}: DescriptionsPanelProps) => {
  const { preferences } = useContext(PreferencesContext);
  const { isFordDirect } = preferences;
  const [currentType, setCurrentType] = useState<string>("CASH");
  const [fieldErrors, setFieldErrors] = useState<Record<string, boolean>>({});

  const handleChangeError = useCallback(
    (id: string, status: boolean) => {
      changeError(id, status);
      setFieldErrors(prev => ({ ...prev, [id]: status }));
    },
    [changeError]
  );

  const hasErrors = Object.values(fieldErrors).some(error => error === true);

  const updateField = useCallback(
    (field: "description" | "disclaimer", value: string) => {
      if (!descriptions) return;

      let updatedDescriptions = descriptions;

      if (field === "description") {
        const currentValue = getTypeDescription(
          currentType,
          updatedDescriptions
        );
        if (value !== currentValue) {
          updatedDescriptions = updateDescription(
            currentType,
            value,
            updatedDescriptions
          );
        }
      } else {
        const currentValue = getTypeDisclaimer(
          currentType,
          updatedDescriptions
        );
        if (value !== currentValue) {
          updatedDescriptions = updateDisclaimer(
            currentType,
            value,
            updatedDescriptions
          );
        }
      }

      onDescriptionsChanged(updatedDescriptions);
    },
    [currentType, onDescriptionsChanged]
  );
  return (
    <div className="descriptions-panel-frame">
      <div className="descriptions-panel-content default-content">
        <Typography variant="h4">
          {Locale.getText("DEFAULT_DESC_DISCLAIM")}
        </Typography>
        <SelectInput
          disabled={hasErrors}
          displayDeselectOption={false}
          id="for-pref-incentive-type"
          label={Locale.getText("INCENTIVE_TYPE") as string}
          name={"incentive-type"}
          options={getIncentiveTypesDropdown(isFordDirect)}
          value={currentType}
          onChange={event => {
            descriptions && setCurrentType(event.target.value as string);
          }}
        />
        <TextArea
          getError={prohibitedContentValidation(
            "pref-description-prohibited",
            (descriptions
              ? getTypeDescription(currentType, descriptions)
              : "") || "",
            handleChangeError
          )}
          label={Locale.getText("DESCRIPTION") as string}
          name={"incentive-description"}
          rows={4}
          value={
            descriptions ? getTypeDescription(currentType, descriptions) : ""
          }
          onChange={event => {
            const currentValue = descriptions
              ? getTypeDescription(currentType, descriptions)
              : "";
            if (event.target.value !== currentValue) {
              updateField("description", event.target.value);
            }
          }}
        />
        <TextArea
          getError={prohibitedContentValidation(
            "pref-disclaimer-prohibited",
            (descriptions
              ? getTypeDisclaimer(currentType, descriptions)
              : "") || "",
            handleChangeError
          )}
          label={Locale.getText("DISCLAIMER") as string}
          name={"incentive-disclaimer"}
          rows={4}
          value={getTypeDisclaimer(currentType, descriptions || [])}
          onChange={event => {
            const currentValue = descriptions
              ? getTypeDisclaimer(currentType, descriptions)
              : "";
            if (event.target.value !== currentValue) {
              updateField("disclaimer", event.target.value);
            }
          }}
        />
      </div>
    </div>
  );
};
